// https://material-ui.com/customization/theming/

import { createMuiTheme } from '@material-ui/core/styles';

// Volvo Novum Light
import VolvoNovumLightEot from '../webfonts/VolvoNovum-Light.eot';
import VolvoNovumLightWoff from '../webfonts/VolvoNovum-Light.woff';
import VolvoNovumLightWoff2 from '../webfonts/VolvoNovum-Light.woff2';
import VolvoNovumLightTtf from '../webfonts/VolvoNovum-Light.ttf';

// Volvo Novum Regular
import VolvoNovumRegularEot from '../webfonts/VolvoNovum-Regular.eot';
import VolvoNovumRegularWoff from '../webfonts/VolvoNovum-Regular.woff';
import VolvoNovumRegularWoff2 from '../webfonts/VolvoNovum-Regular.woff2';
import VolvoNovumRegularTtf from '../webfonts/VolvoNovum-Regular.ttf';

// Volvo Novum Medium
import VolvoNovumMediumEot from '../webfonts/VolvoNovum-Medium.eot';
import VolvoNovumMediumWoff from '../webfonts/VolvoNovum-Medium.woff';
import VolvoNovumMediumWoff2 from '../webfonts/VolvoNovum-Medium.woff2';
import VolvoNovumMediumTtf from '../webfonts/VolvoNovum-Medium.ttf';

// Volvo Novum Bold
import VolvoNovumBoldEot from '../webfonts/VolvoNovum-Bold.eot';
import VolvoNovumBoldWoff from '../webfonts/VolvoNovum-Bold.woff';
import VolvoNovumBoldWoff2 from '../webfonts/VolvoNovum-Bold.woff2';
import VolvoNovumBoldTtf from '../webfonts/VolvoNovum-Bold.ttf';

const volvoNovumLight = {
  fontFamily: 'Volvo Novum',
  fontStyle: 'normal',
  fontDisplay: 'swap',
  fontWeight: 300,
  src: `
    url(${VolvoNovumLightEot}) format('embedded-opentype'),
    url(${VolvoNovumLightWoff}) format('woff'),
    url(${VolvoNovumLightWoff2}) format('woff2'),
    url(${VolvoNovumLightTtf}) format('truetype')
  `
};

// Volvo Novum Regular
const volvoNovumRegular = {
  fontFamily: 'Volvo Novum',
  fontStyle: 'normal',
  fontDisplay: 'swap',
  fontWeight: 400,
  src: `
    url(${VolvoNovumRegularEot}) format('embedded-opentype'),
    url(${VolvoNovumRegularWoff}) format('woff'),
    url(${VolvoNovumRegularWoff2}) format('woff2'),
    url(${VolvoNovumRegularTtf}) format('truetype')
  `
};

// Volvo Novum Medium
const volvoNovumMedium = {
  fontFamily: 'Volvo Novum',
  fontStyle: 'normal',
  fontDisplay: 'swap',
  fontWeight: 500,
  src: `
    url(${VolvoNovumMediumEot}) format('embedded-opentype'),
    url(${VolvoNovumMediumWoff}) format('woff'),
    url(${VolvoNovumMediumWoff2}) format('woff2'),
    url(${VolvoNovumMediumTtf}) format('truetype')
  `
};

// Volvo Novum Bold
const volvoNovumBold = {
  fontFamily: 'Volvo Novum',
  fontStyle: 'normal',
  fontDisplay: 'swap',
  fontWeight: 700,
  src: `
    url(${VolvoNovumBoldEot}) format('embedded-opentype'),
    url(${VolvoNovumBoldWoff}) format('woff'),
    url(${VolvoNovumBoldWoff2}) format('woff2'),
    url(${VolvoNovumBoldTtf}) format('truetype')
  `
};

// Breakpoints
const breakpoints = {
  values: {
    xs: 0,
    sm: 375,
    md: 768,
    lg: 1024,
    xl: 1280,
    xxl: 1920
  }
};

// Theme Helper
const themeHelper = createMuiTheme({
  breakpoints: breakpoints
});

// Normalize.css
const cssBaseline = {
  '@global': {
    '@font-face': [
      volvoNovumLight,
      volvoNovumRegular,
      volvoNovumMedium,
      volvoNovumBold
    ]
  }
};

// Palette
const palette = {
  background: {
    default: '#FFFFFF'
  },
  text: {
    primary: '#141414'
  },
  primary: {
    light: '#537199',
    main: '#284E80',
    dark: '#1C3659',
    contrastText: '#FFFFFF'
  },
  secondary: {
    light: '#4988C7',
    main: '#1C6BBA',
    dark: '#134A82',
    contrastText: '#FFFFFF'
  }
};

// Typography
const typography = {
  fontFamily: "'Volvo Novum', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  // H1
  h1: {
    fontSize: themeHelper.typography.pxToRem(38),
    fontWeight: 500,
    lineHeight: themeHelper.typography.pxToRem(42),
    [themeHelper.breakpoints.up('md')]: {
      fontSize: themeHelper.typography.pxToRem(40),
      lineHeight: themeHelper.typography.pxToRem(44)
    }
  },
  // H2
  h2: {
    fontSize: themeHelper.typography.pxToRem(26),
    fontWeight: 500,
    lineHeight: themeHelper.typography.pxToRem(36),
    [themeHelper.breakpoints.up('md')]: {
      fontSize: themeHelper.typography.pxToRem(32)
    }
  },
  // H3
  h3: {
    fontSize: themeHelper.typography.pxToRem(20),
    fontWeight: 500,
    lineHeight: themeHelper.typography.pxToRem(26)
  },
  // H4
  h4: {
    fontSize: themeHelper.typography.pxToRem(16),
    fontWeight: 500,
    lineHeight: themeHelper.typography.pxToRem(24)
  },
  // Body 1
  body1: {
    fontSize: themeHelper.typography.pxToRem(16),
    fontWeight: 400
  },
  // Body 1
  body2: {
    fontSize: themeHelper.typography.pxToRem(16),
    fontWeight: 400
  },
  // Subtitle 1 (used as: dialogue preheader)
  subtitle1: {
    fontSize: themeHelper.typography.pxToRem(20),
    fontWeight: 500,
    lineHeight: themeHelper.typography.pxToRem(26),
    color: '#707070'
  }
};

// Button
export const button = {
  root: {
    padding: `${themeHelper.typography.pxToRem(
      10
    )} ${themeHelper.typography.pxToRem(40)}`,
    [themeHelper.breakpoints.up('md')]: {
      padding: `${themeHelper.typography.pxToRem(
        15
      )} ${themeHelper.typography.pxToRem(45)}`
    }
  },
  sizeSmall: {
    borderRadius: themeHelper.typography.pxToRem(4),
    padding: `${themeHelper.typography.pxToRem(
      5
    )} ${themeHelper.typography.pxToRem(20)}`,
    fontSize: themeHelper.typography.pxToRem(15)
    //textTransform: 'none'
  },
  sizeLarge: {
    borderRadius: themeHelper.typography.pxToRem(4),
    padding: `${themeHelper.typography.pxToRem(
      10
    )} ${themeHelper.typography.pxToRem(30)}`,
    fontSize: themeHelper.typography.pxToRem(15)
  }
};

// Outlined input
export const outlinedInput = {
  root: {
    borderRadius: 0,
    '&$disabled $notchedOutline': {
      opacity: 0.3
    }
  }
};

// Radio input
export const radio = {
  colorSecondary: {
    color: '#d5d5d5',
    '&$checked': {
      color: '#707070'
    }
  }
};

// Data Grid
export const dataGrid = {
  root: {
    fontSize: themeHelper.typography.pxToRem(16)
  }
};

// Accordion
export const accordion = {
  root: {
    backgroundColor: '#FAFAFA'
  }
};

// Accordion Summary
export const accordionSummary = {
  root: {
    padding: `0 ${themeHelper.typography.pxToRem(20)}`,
    '&$expanded': {
      minHeight: 0
    }
  },
  content: {
    margin: `${themeHelper.typography.pxToRem(20)} 0`,
    '&$expanded': {
      margin: `${themeHelper.typography.pxToRem(20)} 0`
    }
  }
};

// Accordion Details
export const accordionDetails = {
  root: {
    display: 'block',
    padding: `${themeHelper.typography.pxToRem(
      10
    )} ${themeHelper.typography.pxToRem(20)} ${themeHelper.typography.pxToRem(
      20
    )}`
  }
};

// Dialog
export const dialog = {
  paper: {
    width: '80%'
  }
};

// Dialog Title
export const dialogTitle = {
  root: {
    padding: `${themeHelper.typography.pxToRem(
      16
    )} ${themeHelper.typography.pxToRem(24)}`,
    [themeHelper.breakpoints.up('md')]: {
      padding: `${themeHelper.typography.pxToRem(
        50
      )} ${themeHelper.typography.pxToRem(65)} ${themeHelper.typography.pxToRem(
        16
      )} ${themeHelper.typography.pxToRem(65)}`
    }
  }
};

// Dialog Content
export const dialogContent = {
  root: {
    padding: `${themeHelper.typography.pxToRem(
      16
    )} ${themeHelper.typography.pxToRem(24)}`,
    [themeHelper.breakpoints.up('md')]: {
      padding: `${themeHelper.typography.pxToRem(
        16
      )} ${themeHelper.typography.pxToRem(65)}`
    }
  }
};

// Dialog Actions
export const dialogActions = {
  root: {
    justifyContent: 'flex-start',
    padding: `${themeHelper.typography.pxToRem(
      16
    )} ${themeHelper.typography.pxToRem(24)}`,
    [themeHelper.breakpoints.up('md')]: {
      padding: `${themeHelper.typography.pxToRem(
        32
      )} ${themeHelper.typography.pxToRem(65)} ${themeHelper.typography.pxToRem(
        70
      )} ${themeHelper.typography.pxToRem(65)}`
    }
  }
};

// Snackbar
export const snackbarContent = {
  root: {
    backgroundColor: 'transparent',
    color: '#141414'
  },
  message: {
    backgroundColor: 'transparent',
  },
  action: {
    backgroundColor: 'transparent',
  }
};

// Theme
export default createMuiTheme({
  breakpoints: breakpoints,
  palette: palette,
  typography: typography,
  overrides: {
    MuiCssBaseline: cssBaseline,
    MuiButton: button,
    MuiOutlinedInput: outlinedInput,
    MuiAccordionDetails: accordionDetails,
    MuiDataGrid: dataGrid,
    MuiAccordion: accordion,
    MuiAccordionSummary: accordionSummary,
    MuiRadio: radio,
    // MuiDialog: dialog, // not global
    // MuiDialogTitle: dialogTitle, // not global
    // MuiDialogContent: dialogContent, // not global
    // MuiDialogActions: dialogActions, // not global
    MuiSnackbarContent: snackbarContent
  }
});
